import React, { useState, useEffect } from 'react';
import { isEmpty, difference, reduce, flattenDeep } from 'lodash';
import classnames from 'classnames';
import { Accordion, Segment, Checkbox, Dimmer, Loader } from 'semantic-ui-react';
import arrayToTree from 'array-to-tree';

import { useUrlData } from '../assets/hooks';
import { flatten, findKeys } from '../assets';

function Section({
  section,
  activeIndexes,
  selectedCategories,
  resolveSectionState,
  resolveSelectedCategories,
  ...rest
}) {
  const [active, setActive] = useState(false);
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const { expansionControlClasses } = rest;
  const index = section.id;

  function handleSectionStateChange(e, selfProps) {
    const { index } = selfProps;

    resolveSectionState(index);
  }

  function handleCheckboxStateChange(e, selfProps) {
    const { relatedId } = selfProps;

    resolveSelectedCategories(relatedId);
  }

  useEffect(() => {
    if (checked && !selectedCategories.includes(section.id)) {
      resolveSelectedCategories(section.id);
    }
  }, [checked]);

  useEffect(() => {
    const isChecked = selectedCategories.includes(section.id);

    const options = flatten(section, section.options, true);

    const hasOptions = options.length > 0;

    const everyOptionSelected = options.every(s => selectedCategories.includes(s.id));

    const isIndeterminate =
      options.some(s => selectedCategories.includes(s.id)) && !everyOptionSelected;

    setChecked(isChecked || (hasOptions && everyOptionSelected));
    setIndeterminate(isIndeterminate);
  }, [selectedCategories]);

  useEffect(() => {
    const isActive = activeIndexes.includes(index);

    setActive(isActive);
  }, [activeIndexes]);

  const hasNestedOptions = !section.ignoreOptions && !isEmpty(section.options);

  const heading =
    !section.checkbox && !section.parent_id ? (
      <label onClick={() => handleSectionStateChange(null, { index: index })}>{section.name}</label>
    ) : (
      <Checkbox
        relatedId={section.id}
        label={section.name}
        onClick={handleCheckboxStateChange}
        checked={checked}
        indeterminate={indeterminate}
      />
    );

  const title = (
    <Accordion.Title
      active={active}
      has-nested={`${hasNestedOptions}`}
      expand={`${section.checkbox}`}
    >
      {heading}
      {hasNestedOptions && (
        <i
          className={expansionControlClasses}
          onClick={() => handleSectionStateChange(null, { index: index })}
        />
      )}
    </Accordion.Title>
  );

  const selectAllSection = { ...section, checkbox: true, name: 'All', ignoreOptions: true };

  const content = (
    <Accordion.Content active={active}>
      <Sections
        source={
          hasNestedOptions && !section.parent_id
            ? [selectAllSection, ...section.options]
            : section.options
        }
        selectedCategories={selectedCategories}
        resolveSelectedCategories={resolveSelectedCategories}
        expansionControlClasses={expansionControlClasses}
      />
    </Accordion.Content>
  );

  return (
    <>
      {title}
      {content}
    </>
  );
}

function Sections({
  source,
  selectedCategories,
  resolveSelectedCategories,
  expansionControlClasses,
  ...rest
}) {
  const [activeIndexes, setActiveIndexes] = useState([]);

  function resolveSectionState(index = -1, forceClose = false) {
    // const newActiveIndexes = (activeIndexes.includes(index) || forceClose) ? difference(activeIndexes, [index]) : [...activeIndexes, index];
    const newActiveIndexes = activeIndexes.includes(index) || forceClose ? [-1] : [index];
    setActiveIndexes(newActiveIndexes);
  }

  const sections = source.map((section, key) => {
    return (
      <Section
        section={section}
        selectedCategories={selectedCategories}
        activeIndexes={activeIndexes}
        resolveSectionState={resolveSectionState}
        resolveSelectedCategories={resolveSelectedCategories}
        expansionControlClasses={expansionControlClasses}
      />
    );
  });

  return <Accordion {...rest}>{sections}</Accordion>;
}

export function Filter({
  attributes,
  options,
  setOptions,
  selectedCategories,
  setSelectedCategories,
  ...rest
}) {
  const [loading, setLoading] = useState(true);
  const [urlQuery, updateSearch] = useUrlData();

  const { className, blockClass, prefix, filterCriterias, darkMode } = attributes;

  useEffect(() => {
    if (!urlQuery.preselected) {
      return;
    }

    const { preselected, ...newSearch } = urlQuery;
    updateSearch(newSearch);

    const flatten = flattenDeep([preselected]);

    const newSelectedCategories = reduce(
      flatten,
      (result, raw) => {
        const value = parseInt(raw);

        if (value) result.push(value);

        return result;
      },
      [],
    );

    newSelectedCategories && setSelectedCategories(newSelectedCategories);
  }, []);

  useEffect(() => {
    if (!filterCriterias) {
      setLoading(true);
      return;
    }

    const newOptions = JSON.parse(filterCriterias);

    setOptions(newOptions);
    setLoading(false);
  }, [filterCriterias]);

  function resolveSelectedCategories(id = null) {
    if (!id) {
      return;
    }

    const withNestedIds = findKeys(id, options);

    let newSelectedCategories = selectedCategories.includes(id)
      ? difference(selectedCategories, withNestedIds)
      : [...selectedCategories, ...withNestedIds];

    const option = options.find(o => o.id == id);

    if (option && option.parent_id) {
      const parentChildrenIds = findKeys(option.parent_id, options, true);

      const everyParentChildrenSelected = parentChildrenIds.every(pci =>
        newSelectedCategories.includes(pci),
      );

      if (everyParentChildrenSelected) {
        newSelectedCategories = [option.parent_id, ...newSelectedCategories];
      } else {
        newSelectedCategories = newSelectedCategories.filter(id => id != option.parent_id);
      }
    }

    setSelectedCategories(newSelectedCategories);
  }

  const tree = arrayToTree(options, { childrenProperty: 'options' });

  const blockClasses = classnames(blockClass, className, {});

  const expansionControlClasses = classnames(`expansion-control`, `fal fa-plus`);

  return (
    <div className={blockClasses} {...rest}>
      <Segment inverted={darkMode}>
        <Dimmer active={loading}>
          <Loader>Loading</Loader>
        </Dimmer>
        <Sections
          source={tree}
          selectedCategories={selectedCategories}
          resolveSelectedCategories={resolveSelectedCategories}
          inverted={darkMode}
          expansionControlClasses={expansionControlClasses}
        />
      </Segment>
    </div>
  );
}
